import cookie from 'js-cookie';
import { Base64 } from 'js-base64';
import { CACHE_VERSION } from '../configs/public.config';

const isBrowser = typeof window !== 'undefined';

type CacheOptions = {
  /** 缓存的命名空间，在此处会将namespace作为cookie / localStorage前缀，方便管理 */
  namespace: string;
  /** 缓存的加密方式，默认为base64  */
  encryption: 'base64';
};

class Cache {
  private options: CacheOptions;
  constructor(options?: CacheOptions | undefined) {
    const defaultOptions = {
      namespace: CACHE_VERSION || 'default',
      encryption: 'base64',
    };

    this.options = Object.assign(defaultOptions, options);
  }

  __encrypt(value: string): string {
    if (value) {
      return this.options.encryption === 'base64' ? Base64.encode(value) : value;
    }
    return value;
  }

  __decrypt(value: string | undefined | null) {
    if (value) {
      return this.options.encryption === 'base64' ? Base64.decode(value) : value;
    }
    return value;
  }

  /** 设置缓存到localStorage，如果设备不支持设置到cookie */
  set(key: string, value: string | number) {
    if (isBrowser) {
      key = this.options.namespace + '_' + key;
      value = this.__encrypt(value + '');
      if (window.localStorage) {
        window.localStorage.setItem(key, value);
      } else {
        cookie.set(key, value, { expires: new Date(Date.now() + 99999999999), secure: true });
      }
    }
  }

  /** 从localStorage获取值，如果设备不支持localstorage，从cookie获取 */
  get(key: string) {
    if (isBrowser) {
      key = this.options.namespace + '_' + key;
      let value;
      if (window.localStorage) {
        value = window.localStorage.getItem(key);
      } else {
        value = cookie.get(key);
      }
      value = this.__decrypt(value);
      return value;
    }
    return undefined;
  }

  getWithoutDecrypt(key: string) {
    let value;
    if (window.localStorage) {
      value = window.localStorage.getItem(key);
    } else {
      value = cookie.get(key);
    }
    return value;
  }

  setCookie(key: string, value: string, options: cookie.CookieAttributes = {}) {
    if (isBrowser) {
      if (window.location.host.includes('.insta360.com')) {
        options.expires = options.expires || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
        options.domain = '.insta360.com';
        options.secure = true;
      }
      cookie.set(key, value, options);
    }
  }

  getCookie(key: string) {
    return cookie.get(key);
  }

  removeCookie(key: string, options: cookie.CookieAttributes = {}) {
    if (isBrowser && !options.domain) {
      if (window.location.hostname.includes('.insta360.com')) {
        options.domain = '.insta360.com';
      } else {
        options.domain = window.location?.hostname || '';
      }
    }
    cookie.remove(key, options);
  }

  setSessionItem(key: string, value: string) {
    if (isBrowser && window.sessionStorage) {
      window.sessionStorage.setItem(key, value);
    }
  }

  getSessionItem(key: string) {
    if (isBrowser && window.sessionStorage) {
      return window.sessionStorage.getItem(key);
    }
    return undefined;
  }

  removeSessionItem(key: string) {
    if (isBrowser && window.sessionStorage) {
      window.sessionStorage.removeItem(key);
    }
  }

  remove(key: string) {
    if (isBrowser) {
      key = this.options.namespace + '_' + key;
      if (window.localStorage) {
        window.localStorage.removeItem(key);
      } else {
        // 默认删除与当前页面path匹配的 cookie
        cookie.remove(key);
      }
    }
  }

  /** 获取所有的cookie，localStorage直接通过window.localStorage获取 */
  gets() {
    if (isBrowser) {
      return cookie.get();
    }
    return {};
  }

  removes(keys: string[]) {
    if (keys.constructor === Array && keys.length > 0) {
      let i;
      let key;
      for (i = 0; i < keys.length; i++) {
        key = keys[i];
        this.remove(key);
      }
    }
  }
  /** 判断当前存储的时间戳是否过期 */
  isExpired(key: string) {
    const date = this.get(key);
    if (date && new Date().getTime() < Number(date)) {
      return false;
    }
    return true;
  }
}

export default Cache;
